<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { createFilledArray } from "@/helpers/utility/Array";
import { formatRoundName } from "../simulationRound/useRoundTime";

import { useCondition } from "./useCondition";
import { useSettingsStore } from "@editor/store/settings.store";

// import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";

import type { ConditionItem } from "./ConditionItem";
import { getValidActivatorEndTypes } from "./conditionFunctions";
import { notNull } from "@/helpers/utility/Type";

interface Props {
  conditionId: number;
}
const props = defineProps<Props>();

const ctx = useCondition(() => props.conditionId);
const { t } = useI18n();
const settingsStore = useSettingsStore();

const allAvailableRoundIndices = computed(() => {
  return createFilledArray(settingsStore.simulationRoundAmount);
});

const usedTimeForm = computed(() => {
  if (ctx.activatorTimeForm == "point") return "point";
  return ctx.condition.activatorEndType == null ? "point" : "range";
});

const needsTime = computed(() => {
  if (
    (
      ["valueChange", "increase", "decrease", "activationCount"] as (
        | string
        | null
      )[]
    ).includes(ctx.condition.activatorProperty)
  ) {
    return true;
  }
  if (ctx.condition.activatorType == "roundRange") {
    return true;
  }

  return false;
});

const rangeOptional = computed(() => {
  if (!ctx.supportsActivatorTime) return false;
  if (ctx.activatorTimeForm == "point") return false;
  if (ctx.condition.activatorType == "roundRange") return false;
  return true;
});

const startVisible = computed(() => {
  if (!ctx.supportsActivatorTime) {
    return false;
  }
  if (needsTime.value) return true;
  return true;
});

const validActivatorStartTypeOptions = computed(() => {
  return ctx.validActivatorStartTypes.map((type) => ({
    value: type,
    label: t(`labels.conditionTimeType.${type}`)
  }));
});
const selectedActivatorStartTypeOption = computed(() => {
  return validActivatorStartTypeOptions.value.find(
    (o) => o.value == ctx.condition.activatorStartType
  );
});

const startOptions = computed(() => {
  return allAvailableRoundIndices.value.filter((i) =>
    ctx.condition.activatorEnd ? i <= ctx.condition.activatorEnd : true
  ).map((i) => ({
    value: i,
    label: formatRoundIndex(i)
  }));
});

const selectedStartOption = computed(() => {
  return startOptions.value.find(
    (o) => o.value == ctx.condition.activatorStart
  );
});

function setStartType(type: ConditionItem["activatorStartType"]) {
  const startValue = type == "relativeRound" ? 1 : 0;
  ctx.validUpdate({
    activatorStartType: type,
    activatorStart: startValue
  });
}

function setStart(value: number) {
  const changes: Partial<ConditionItem> = {
    activatorStart: value
  };

  if (ctx.condition.activatorStartType == ctx.condition.activatorEndType) {
    if (ctx.condition.activatorStartType == "absoluteRound") {
      if (
        ctx.condition.activatorEnd != null &&
        ctx.condition.activatorEnd < value
      ) {
        changes.activatorEnd = value;
      }
    }
  }

  ctx.validUpdate(changes);
}

function updateTimeForm(form: "range" | "point") {
  if (form == "range") {
    ctx.validUpdate({
      activatorEndType: "currentRound",
      activatorEnd: 0
    });
  } else {
    ctx.validUpdate({
      activatorEndType: null
    });
  }
}

const endVisible = computed(() => {
  // there's only an end if there's a start.
  if (!startVisible.value) return;

  return usedTimeForm.value == "range";
});

const endOptions = computed(() => {
  let filtered = allAvailableRoundIndices.value;
  if (
    ctx.condition.activatorStartType == "startRound" ||
    ctx.condition.activatorStartType == "currentRound"
  ) {
    // don't filter
  } else {
    filtered = filtered.filter(
      (i) => i >= (ctx.condition.activatorStart || 0)
    );
  }
  return filtered.map((i) => ({
    value: i,
    label: formatRoundIndex(i)
  }));
});
const selectedEndOption = computed(() => {
  return endOptions.value.find((o) => o.value == ctx.condition.activatorEnd);
});
const endTypeOptions = computed(() => {
  return getValidActivatorEndTypes(ctx.condition).filter(notNull).map((type) => ({
    value: type,
    label: t(`labels.conditionTimeType.${type}`)
  }));
});
const selectedEndTypeOption = computed(() => {
  return endTypeOptions.value.find(
    (o) => o.value == ctx.condition.activatorEndType
  );
});

const roundUnit = computed(() => settingsStore.simulationRoundUnit);

const roundUnitName = computed(() => {
  return t(`roundUnits.${roundUnit.value}`);
});

function formatRoundIndex(index: number) {
  if (index == null) {
    console.error("index is null", index);
    return "";
  }
  return formatRoundName(
    settingsStore.simulationDurationType,
    settingsStore.simulationRoundUnit,
    settingsStore.simulationRoundLength,
    index,
    settingsStore.simulationTimeStart,
    false
  );
}
</script>

<template>
  <div
    v-if="ctx.supportsActivatorTime && ctx.activatorComplete"
    class="column pt-2"
  >
    <!-- <input-checkbox
      v-if="timeOptional && ctx.condition.activatorType"
      :value="ctx.condition.startType == null"
      :label="
        ctx.timeForm == 'range'
          ? $t('labels.conditionInCurrentTimeRange')
          : $t('labels.conditionInCurrentRound')
      "
      @update:value="
        ctx.validUpdateKey(
          'startType',
          $event
            ? null
            : ctx.timeForm == 'range'
            ? 'startRound'
            : 'currentRound'
        )
      "
    /> -->

    <input-state-toggle
      v-if="rangeOptional"
      v-slot="{ state }"
      :states="['point', 'range']"
      :value="usedTimeForm"
      @update:value="updateTimeForm($event)"
    >
      {{ $t(`labels.conditionTimeForm.${state}`) }}
    </input-state-toggle>

    <div v-if="startVisible">
      <div
        :class="
          ctx.condition.activatorStartType == 'startRound' ||
          ctx.condition.activatorStartType == 'currentRound'
            ? 'line'
            : 'r-50-50'
        "
      >
        <input-dropdown
          v-if="ctx.validActivatorStartTypes.length > 1"
          :value="selectedActivatorStartTypeOption"
          :options="validActivatorStartTypeOptions"
          :label="
            usedTimeForm == 'range'
              ? $t('labels.conditionStart')
              : $t('labels.conditionTime')
          "
          :searchable="true"
          prop="label"
          item-key="value"
          class="w-full"
          @update:value="setStartType($event.value)"
        ></input-dropdown>
        <input-dropdown
          v-if="ctx.condition.activatorStartType == 'absoluteRound'"
          :options="startOptions"
          :value="selectedStartOption"
          :label="roundUnitName"
          :searchable="true"
          :clear-on-select="false"
          prop="label"
          item-key="value"
          @update:value="setStart($event.value)"
        ></input-dropdown>
        <input-number
          v-else-if="ctx.condition.activatorStartType == 'relativeRound'"
          :label="$t(`roundUnits.${roundUnit}`, 4)"
          :value="ctx.condition.activatorStart"
          :min="1"
          @update:value="setStart($event)"
        />
      </div>
    </div>

    <div v-if="endVisible">
      <div
        :class="
          ctx.condition.activatorEndType == 'currentRound' ? 'line' : 'r-50-50'
        "
      >
        <input-dropdown
          v-if="endTypeOptions.length > 1"
          :value="selectedEndTypeOption"
          :options="endTypeOptions"
          :label="$t('labels.conditionEnd')"
          :searchable="true"
          class="w-full"
          prop="label"
          item-key="value"
          @update:value="ctx.validUpdateKey('activatorEndType', $event.value)"
        ></input-dropdown>
        <input-dropdown
          v-if="ctx.condition.activatorEndType == 'absoluteRound'"
          :options="endOptions"
          :value="selectedEndOption"
          :label="roundUnitName"
          :searchable="true"
          prop="label"
          item-key="value"
          @update:value="ctx.validUpdateKey('activatorEnd', $event.value)"
        />
        <input-number
          v-else-if="ctx.condition.activatorEndType == 'relativeRound'"
          :value="ctx.condition.activatorEnd"
          :label="$t(`roundUnits.${roundUnit}`, 4)"
          :min="1"
          @update:value="ctx.validUpdateKey('activatorEnd', $event)"
        />
      </div>
    </div>
  </div>
</template>
